import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Stack,
  Tag,
  VStack,
} from '@chakra-ui/react';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { FinanceInformationValues } from '@common/models/InvestorAccountModel/types';
import currency from '@common/utility/currency';
import React, { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

export const FinancesForm: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>();
  const { investorAccount, updateFinanceInformation } = useInvestorAccountModel();
  const survey = investorAccount?.non_accredited_survey_v2 ?? null;

  const form = useForm<FinanceInformationValues>({
    defaultValues: {
      annualIncome: survey?.annual_income?.value,
      liquidRatio: 100 - (survey?.pct_liquid_securities?.value ?? 0),
      netWorth: survey?.net_worth?.value,
    },
  });

  const liquidRatioValue = useWatch({ control: form.control, name: 'liquidRatio' });

  const save = form.handleSubmit(async ({ annualIncome, liquidRatio = 50, netWorth }) => {
    setIsSaving(true);
    setFormError(null);

    const result = await updateFinanceInformation({
      annualIncome: annualIncome,
      netWorth: netWorth,
      pct_illiquid_securities: liquidRatio,
      pct_liquid_securities: 100 - liquidRatio,
    });

    if (result.error) {
      form.reset();
      setIsSaving(false);
      setFormError(result.error);
    } else {
      form.reset({
        annualIncome,
        liquidRatio,
        netWorth,
      });
      setIsSaving(false);
    }
  });

  const needsAttention =
    survey?.net_worth?.value === undefined ||
    survey?.net_worth?.value === null ||
    survey?.annual_income?.value === undefined ||
    survey?.annual_income?.value === null ||
    survey?.pct_liquid_securities?.value === undefined ||
    survey?.pct_liquid_securities?.value === null ||
    survey?.pct_illiquid_securities?.value === undefined ||
    survey?.pct_illiquid_securities?.value === null;

  return (
    <Grid templateColumns={['1fr 1fr']} gap={25} w="100%">
      <GridItem colSpan={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} spacing={[2, 5]}>
          <Heading id="finances" fontSize={'21px'}>
            Finances
          </Heading>
          {needsAttention && <Tag colorScheme={'yellow'}>Needs Attention</Tag>}
        </Stack>
        {form.formState.isDirty &&
          (isSaving ? (
            <Spinner color="blue.500" size="md" thickness="4px" />
          ) : (
            <HStack spacing={5}>
              <Button onClick={save}>Save</Button>
              <Button variant="link" onClick={() => form.reset()}>
                Cancel
              </Button>
            </HStack>
          ))}
      </GridItem>
      {formError && (
        <GridItem colSpan={2}>
          <Alert status="error">
            <AlertIcon />
            {formError}
          </Alert>
        </GridItem>
      )}
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      {/* Annual Income */}
      <GridItem colSpan={[2, 1]}>
        <FormControl>
          <FormLabel htmlFor="annualIncome">What was your income this last year?</FormLabel>
          <FormHelperText>
            Please consider all income sources.{' '}
            <Popover>
              <PopoverTrigger>
                <Button variant={'link'} size="xs">
                  Why do we ask this?
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody p="20px">
                  We ask these questions about your annual income and net worth because the SEC limits how much
                  individual investors can invest in a Regulation A+ offering. The SEC limits individuals to investing
                  no more than 10% of the greater of the person's annual income or net worth. They can be calculated
                  alone or together with a spouse. Net worth does not include the value of the person's primary
                  residence and any loans secured by the residence (up to the value of the residence).
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </FormHelperText>
        </FormControl>
      </GridItem>
      <GridItem
        colSpan={[2, 1]}
        display="flex"
        justifyContent="center"
        alignItems={['center', 'end']}
        flexDirection="column"
        gap={2}>
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.annualIncome)}>
          <Controller
            control={form.control}
            name="annualIncome"
            rules={{
              max: {
                message: 'Annual income must be less than or equal to $2,147,483,647',
                value: 2147483647,
              },
              min: 0,
            }}
            render={({ field }) => (
              <NumberInput
                inputMode="numeric"
                precision={0}
                isValidCharacter={(character) => !isNaN(Number(character))}
                onChange={field.onChange}
                value={currency.format(field.value ?? 0, { maximumFractionDigits: 0 })}
                step={5000}>
                <NumberInputField maxLength={20} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          />
          {form.formState.errors.annualIncome && (
            <FormErrorMessage>{form.formState.errors.annualIncome.message}</FormErrorMessage>
          )}
        </FormControl>
      </GridItem>
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      {/* Net Worth */}
      <GridItem colSpan={[2, 1]}>
        <FormControl>
          <FormLabel htmlFor="netWorth">What is your current net worth?</FormLabel>
          <FormHelperText>
            Please consider all income sources.{' '}
            <Popover>
              <PopoverTrigger>
                <Button variant={'link'} size="xs">
                  Why do we ask this?
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody p="20px">
                  We ask these questions about your annual income and net worth because the SEC limits how much
                  individual investors can invest in a Regulation A+ offering. The SEC limits individuals to investing
                  no more than 10% of the greater of the person's annual income or net worth. They can be calculated
                  alone or together with a spouse. Net worth does not include the value of the person's primary
                  residence and any loans secured by the residence (up to the value of the residence).
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </FormHelperText>
        </FormControl>
      </GridItem>
      <GridItem
        colSpan={[2, 1]}
        display="flex"
        justifyContent="center"
        alignItems={['center', 'end']}
        flexDirection="column"
        gap={2}>
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.netWorth)}>
          <Controller
            control={form.control}
            name="netWorth"
            rules={{
              max: {
                message: 'Net worth must be less than or equal to $2,147,483,647',
                value: 2147483647,
              },
              min: 0,
            }}
            render={({ field }) => (
              <NumberInput
                onChange={field.onChange}
                isValidCharacter={(character) => !isNaN(Number(character))}
                value={currency.format(field.value ?? 0, { maximumFractionDigits: 0 })}
                step={5000}>
                <NumberInputField maxLength={20} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          />
          {form.formState.errors.netWorth && (
            <FormErrorMessage>{form.formState.errors.netWorth.message}</FormErrorMessage>
          )}
        </FormControl>
      </GridItem>
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      {/* Financial Makeup */}
      <GridItem colSpan={[2, 1]}>
        <FormControl>
          <FormLabel htmlFor="liquidRatio">What's your current financial makeup?</FormLabel>
          <FormHelperText>It's OK to take your best guess.</FormHelperText>
        </FormControl>
      </GridItem>
      <GridItem colSpan={[2, 1]}>
        <FormControl isDisabled={isSaving}>
          <VStack>
            <Controller
              control={form.control}
              name="liquidRatio"
              render={({ field }) => (
                <Slider
                  isDisabled={isSaving}
                  aria-label="slider-ex-4"
                  value={field.value}
                  onChange={(v) => field.onChange(v)}>
                  <SliderTrack bg="green.300" _disabled={{ bg: 'green.300' }}>
                    <SliderFilledTrack bg="blue.600" />
                  </SliderTrack>
                  <div style={{ height: 24 }}>
                    {!isSaving ? <SliderThumb boxSize={8} borderColor="gray.100" /> : null}
                  </div>
                </Slider>
              )}
            />
          </VStack>
          <HStack justify={'space-between'} w="full" spacing={20}>
            <VStack color="blue.600" align="start">
              <TextBlock color="blue.600" weight="bold" fontSize={'24px'}>
                {liquidRatioValue}%
              </TextBlock>
              <TextBlock color="blue.600" weight="bold" fontSize={'12px'}>
                Illiquid
              </TextBlock>
              <TextBlock color="blue.600" fontSize={'11px'}>
                Physical Real Estate, Private Equity, Non-traded REITs, Fine Art
              </TextBlock>
            </VStack>
            <VStack textAlign="right" align="end">
              <TextBlock color="green" weight="bold" fontSize={'24px'}>
                {100 - (liquidRatioValue ?? 50)}%
              </TextBlock>
              <TextBlock color="green" weight="bold" fontSize={'12px'}>
                Liquid
              </TextBlock>
              <TextBlock color="green" fontSize={'11px'}>
                PCash & Savings, Venmo, etc. Stocks, Bonds, Crypto
              </TextBlock>
            </VStack>
          </HStack>
        </FormControl>
      </GridItem>
    </Grid>
  );
};
